import { MessageSenders } from 'api/supportAssistant'
import { ITEM_STATUS_CANCELLED, ITEM_STATUS_AWAITING_DATES } from 'constants/cart'
import { isLEHotel } from 'lib/offer/offerTypes'
import { canAddInsuranceToOrder, isHotelOrderItem, getIsCharterBundleOrder } from 'lib/order/orderUtils'
import { isGroupPaymentOrder } from 'lib/payment/groupPaymentDetailsUtils'
import { canChangeHotelRooms } from './orderUtils'
import { getExtraGuestCapacity } from 'lib/order/extraGuestUtils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import config from 'constants/config'
import moment from 'moment'

export const isSenderSystem = (sender: App.ChatMessageSender) => sender === 'system'

export const isSenderUser = (sender: App.ChatMessageSender) => sender === 'user'

export const isSenderLE = (sender: App.ChatMessageSender) => sender === 'agent' || sender === 'chatbot'

export interface BuildChatMessageParams {
  chatMessageMeta?: App.ChatMessageMeta
  currentChatId: string | undefined
  account?: App.AuthAccount
  customerId?: string
  hasUpcomingBooking: boolean
  canViewLuxPlusBenefits: boolean
  hasLoyaltyPrioritySupport?: boolean
  loyaltyAccountTier?: App.LoyaltyTier
  luxPlusContact: {
    number: string | undefined
    numberHumanReadable: string | undefined
  }
  loyaltyContact?: {
    number: string | undefined
    numberHumanReadable: string | undefined
  }
  message: string
  locale?: string
  isCurrentOrder?: boolean
  orderBookingNumbers?: Array<App.OrderBookingNumber>
  ordersWithAirportTransfers?: Array<App.OrderWithAirportTransfers>
  upcomingOrdersWithAirportTransfers?: Array<App.OrderWithAirportTransfers>
  lesaNoAnswerAgentHandoffVariation: App.LesaNoAnswerAgentHandoffVariation
  lesaBookingPickerVariation: App.LesaBookingPickerVariation
}

// TODO: WRITE UNIT TESTS
export function buildChatMessagePayload(params: BuildChatMessageParams): Partial<App.ChatMessage> {
  const {
    chatMessageMeta,
    currentChatId,
    account,
    customerId,
    hasUpcomingBooking,
    canViewLuxPlusBenefits,
    hasLoyaltyPrioritySupport,
    loyaltyAccountTier,
    luxPlusContact,
    loyaltyContact,
    message,
    locale,
    isCurrentOrder,
    orderBookingNumbers,
    ordersWithAirportTransfers,
    upcomingOrdersWithAirportTransfers,
    lesaNoAnswerAgentHandoffVariation,
    lesaBookingPickerVariation,
  } = params

  const trimmedMessage = message.trim()

  // Find the matching orderBookingNumber to lift its self-service flags to the root payload
  const orderId = chatMessageMeta?.payload?.id_orders
  const matchingBookingNumber = orderId
    ? orderBookingNumbers?.find(obn => obn.id === orderId)
    : orderBookingNumbers?.[0] // single order, use the first one

  const messageMeta = chatMessageMeta ? {
    action: chatMessageMeta.action,
    payload: {
      ...chatMessageMeta.payload,
      chatId: currentChatId,
      customerId,
      email: chatMessageMeta.payload?.email ?? account?.email,
      phone: chatMessageMeta.payload?.phone ?? account?.phone,
      firstName: chatMessageMeta.payload?.firstName ?? account?.givenName,
      lastName: chatMessageMeta.payload?.lastName ?? account?.surname,
      hasUpcomingBooking,
      isLuxPlusMember: canViewLuxPlusBenefits,
      hasLoyaltyPrioritySupport,
      loyaltyAccountTier,
      luxPlusContact,
      loyaltyContact,
      isCurrentOrder: isCurrentOrder || chatMessageMeta.payload?.isCurrentOrder,
      orderBookingNumbers,
      ordersWithAirportTransfers,
      upcomingOrdersWithAirportTransfers,
      lesaNoAnswerAgentHandoffVariation,
      lesaBookingPickerVariation,
      // Lift self-service flags from orderBookingNumbers to root payload
      canEditOrder: chatMessageMeta.payload?.canEditOrder ?? matchingBookingNumber?.canEditOrder,
      canChangeDates: chatMessageMeta.payload?.canChangeDates ?? matchingBookingNumber?.canChangeDates,
      canBeCancelled: chatMessageMeta.payload?.canBeCancelled ?? matchingBookingNumber?.canBeCancelled,
      canRequestRefund: chatMessageMeta.payload?.canRequestRefund ?? matchingBookingNumber?.canRequestRefund,
      canRefundToOrigin: chatMessageMeta.payload?.canRefundToOrigin ?? matchingBookingNumber?.canRefundToOrigin,
      canRefundToCredit: chatMessageMeta.payload?.canRefundToCredit ?? matchingBookingNumber?.canRefundToCredit,
      changeOfMindEndDate: chatMessageMeta.payload?.changeOfMindEndDate ?? matchingBookingNumber?.changeOfMindEndDate,
      canRequestChangeDates: chatMessageMeta.payload?.canRequestChangeDates ?? matchingBookingNumber?.canRequestChangeDates,
      canDownloadInvoice: chatMessageMeta.payload?.canDownloadInvoice ?? matchingBookingNumber?.canDownloadInvoice,
      canShareBooking: chatMessageMeta.payload?.canShareBooking ?? matchingBookingNumber?.canShareBooking,
      hasMapCoordinates: chatMessageMeta.payload?.hasMapCoordinates ?? matchingBookingNumber?.hasMapCoordinates,
      canAddSpecialRequest: chatMessageMeta.payload?.canAddSpecialRequest ?? matchingBookingNumber?.canAddSpecialRequest,
      canChangeGuestName: chatMessageMeta.payload?.canChangeGuestName ?? matchingBookingNumber?.canChangeGuestName,
      canAddExtraGuests: chatMessageMeta.payload?.canAddExtraGuests ?? matchingBookingNumber?.canAddExtraGuests,
      canChangeRooms: chatMessageMeta.payload?.canChangeRooms ?? matchingBookingNumber?.canChangeRooms,
      canExtendDates: chatMessageMeta.payload?.canExtendDates ?? matchingBookingNumber?.canExtendDates,
      hasCarHire: chatMessageMeta.payload?.hasCarHire ?? matchingBookingNumber?.hasCarHire,
      canUpsellInsurance: chatMessageMeta.payload?.canUpsellInsurance ?? matchingBookingNumber?.canUpsellInsurance,
      daysBeforeCheckInChangesDisallowed: chatMessageMeta.payload?.daysBeforeCheckInChangesDisallowed ?? matchingBookingNumber?.daysBeforeCheckInChangesDisallowed,
      freeCancellationPeriodEndDate: chatMessageMeta.payload?.freeCancellationPeriodEndDate ?? matchingBookingNumber?.freeCancellationPeriodEndDate,
      checkInDate: chatMessageMeta.payload?.checkInDate ?? matchingBookingNumber?.startDate,
    },
  } : {
    payload: { customerId, lesaNoAnswerAgentHandoffVariation, lesaBookingPickerVariation },
  }

  return {
    text: trimmedMessage,
    chatId: currentChatId,
    locale,
    customer: {
      countryCode: account?.phonePrefix,
      email: account?.email,
      phone: account?.phone,
      firstName: account?.givenName,
      lastName: account?.surname,
    },
    meta: messageMeta,
    sender: MessageSenders.USER,
  }
}

export const buildChatMessageMetaPayload = (
  order: App.Order,
  item: App.OrderItem | App.OrderBedbankItem,
  offer: App.BedbankOffer | App.Offer,
  options: Partial<App.ChatMessageMetaPayload>,
  cancellationConfig?: App.OrderItemHotelCancellationConfig,
  isSelfServiceNameChangeEnabled = false,
): App.ChatMessageMetaPayload => {
  const hasFlight = !!order.hasFlight
  const hasTransfers = !!order.transferItems.filter(transfer => transfer.status !== ITEM_STATUS_CANCELLED).length
  const hasCarHire = !!order.carHireItems.filter(transfer => transfer.status !== ITEM_STATUS_CANCELLED).length
  const canUpsellInsurance = !order.insuranceItems.length && canAddInsuranceToOrder(order, order.regionCode, offer)
  const canEditOrder = !!order.accessDetails.canEdit

  let extraGuestDetails = null
  let extraGuestCapacity = null
  if (isHotelOrderItem(item)) {
    const packageKey = getPackageUniqueKey(item.package.id, item.package.duration, (item.package as App.OrderItemHotelPackage).roomRateId)
    extraGuestCapacity = getExtraGuestCapacity(offer as App.HotelOffer, item, packageKey)
    extraGuestDetails = {
      maxCapacity: extraGuestCapacity,
    }
  }

  const awaitingDates = item.status === ITEM_STATUS_AWAITING_DATES

  const isCharterOrder = getIsCharterBundleOrder(order)
  const canDownloadInvoice = Boolean(config.ORDER_TAX_INVOICE_FILENAME) && !isCharterOrder

  const property = offer?.property
  const hasMapCoordinates = !!(property?.latitude && property?.longitude)

  const isHotelItem = isHotelOrderItem(item)
  const isCancelled = item.status === ITEM_STATUS_CANCELLED
  const hotelItem = isHotelItem ? item : undefined
  const isBeforeCheckIn = hotelItem?.reservation?.startDate ? moment().isBefore(moment(hotelItem.reservation.startDate)) : false
  const canAddSpecialRequest = isHotelItem && isBeforeCheckIn && !isCancelled && canEditOrder

  const canShareBooking = order.isBookingOwner
  const canChangeGuestName = isSelfServiceNameChangeEnabled && (cancellationConfig?.canChangeGuestName ?? false)

  const payload: App.ChatMessageMetaPayload = {
    awaitingDates,
    bookingNumber: item.bookingNumber,
    canBeCancelled: false,
    canCarHireBeCancelled: false,
    canChangeDates: false,
    canRequestChangeDates: false,
    canChangeRooms: false,
    canExtendDates: false,
    canRequestRefund: cancellationConfig?.canRequestRefund ?? false,
    canRefundToOrigin: cancellationConfig?.canRefundToOrigin ?? false,
    canRefundToCredit: cancellationConfig?.canRefundToCredit ?? false,
    changeOfMindEndDate: cancellationConfig?.changeOfMindEndDate ?? undefined,
    canAddExtraGuests: options.canAddExtraGuests,
    extraGuestDetails,
    customerId: order.customerId,
    daysBeforeCheckInChangesDisallowed: 0,
    hasFlight,
    hasTransfers,
    hasCarHire,
    canUpsellInsurance,
    id_orders: order.id,
    itemId: item.id,
    orderType: options.orderType,
    canEditOrder,
    canDownloadInvoice,
    canShareBooking,
    hasMapCoordinates,
    canAddSpecialRequest,
    canChangeGuestName,
    ...options,
  }

  // For now, change rooms is only available for FLASH. Not BedBank, Not sure about LPC
  if (isLEHotel(offer)) {
    payload.canChangeRooms = options.canChangeRooms || canChangeHotelRooms(order, offer, order.items, item as App.OrderItem, cancellationConfig)
    payload.daysBeforeCheckInChangesDisallowed = offer?.daysBeforeCheckInChangesDisallowed ?? 0
  }

  if (isGroupPaymentOrder(order)) {
    payload.canChangeDates = false
    payload.canExtendDates = false
    payload.canRequestChangeDates = false
    payload.canAddExtraGuests = false
    payload.canChangeRooms = false
  }

  return payload
}
